import { useFocusEffect, useGlobalSearchParams, useRouter } from 'expo-router';
import { getErrorScreen } from 'features/auth/hooks/useAcceptChallenge';
import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import useRedirect from 'features/otp/hooks/useRedirection/useRedirection';
import useVerifyChallenge from 'features/otp/hooks/useVerifyChallenge/useVerifyChallenge';
import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';

type AcceptTwoFAParams = {
  emailAddress: string;
  code: string;
  session: string;
};

const AcceptTwoFA = () => {
  const router = useRouter();
  const { verifyChallenge, isLoading: isVerifyChallengeLoading } =
    useVerifyChallenge();
  const { emailAddress, code, session } =
    useGlobalSearchParams<AcceptTwoFAParams>();
  const { segmentClient } = useAnalyticsConfiguration();
  const { challengeRedirect } = useRedirect();

  const onCodeSubmitAction = useCallback(async () => {
    const anonymousId = await segmentClient.getAnonymousId();

    if (session) {
      const result = await verifyChallenge(
        {
          emailAddress: emailAddress ?? '',
          code: code,
          phoneNumber: '',
          anonymousId: anonymousId ?? '',
          session: session ?? '',
        },
        {},
      );

      // We should always get a challenge in the result being we are in the 2FA flow.
      if (result?.data?.challenge) {
        await challengeRedirect(
          result.data.challenge,
          emailAddress ?? '',
          '',
          result.data.phoneLineNumber ?? '',
          result.data.session ?? '',
          undefined,
          OtpFlowKey.FORGOT_PASSWORD_2FA,
        );
      } else {
        // If we don't have a challenge, we can assume there is some sort of error
        router.navigate({
          pathname: '/otp/error',
          params: {
            screenType: getErrorScreen(String(result?.errorCode)),
            message: result?.message,
            title: result?.title,
          },
        });
      }
    }
  }, [
    segmentClient,
    session,
    verifyChallenge,
    emailAddress,
    code,
    challengeRedirect,
    router,
  ]);

  useFocusEffect(
    useCallback(() => {
      void onCodeSubmitAction();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
  );

  return (
    <View style={styles.container}>
      <ScreenTransitionOverlay
        isLoading={isVerifyChallengeLoading}
        LoadingComponent={<FullScreenLoading isCoinLoader />}
        ErrorComponent={null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

export default AcceptTwoFA;
